import type { MediaDeviceFailure } from 'livekit-client';

export interface MediaAccessIssue {
  title: string;
  message: string;
  detail: string;
}

export function createMediaDeviceFailureError(
  failure?: MediaDeviceFailure,
  kind?: MediaDeviceKind
) {
  const error = new Error(
    `Media device failure${failure ? `: ${failure}` : ''}${kind ? ` (${kind})` : ''}`
  );

  if (failure === 'PermissionDenied') {
    error.name = 'NotAllowedError';
  } else if (failure === 'NotFound') {
    error.name = 'NotFoundError';
  } else if (failure === 'DeviceInUse') {
    error.name = 'NotReadableError';
  } else {
    error.name = 'MediaDevicesError';
  }

  return error;
}

export function getMediaAccessIssue(err: Pick<Error, 'name' | 'message'>): MediaAccessIssue | null {
  const name = err.name || '';
  const message = err.message || '';
  const normalized = `${name} ${message}`.toLowerCase();

  if (
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    normalized.includes('permission') ||
    normalized.includes('denied') ||
    normalized.includes('notallowed')
  ) {
    return {
      title: 'Camera and microphone access is blocked',
      message: 'Your browser did not allow Life Drawing Live to use your camera or microphone.',
      detail: 'Choose “Try camera/mic again” to ask the browser again. If no prompt appears, use the lock or site settings icon in the address bar to allow camera and microphone access, then try again.',
    };
  }

  if (
    name === 'NotFoundError' ||
    name === 'DevicesNotFoundError' ||
    normalized.includes('device not found') ||
    normalized.includes('no camera') ||
    normalized.includes('no microphone')
  ) {
    return {
      title: 'No camera or microphone was found',
      message: 'You can still enter the room without publishing audio or video.',
      detail: 'Connect a device and choose “Try camera/mic again”, or continue without camera and microphone.',
    };
  }

  if (
    name === 'NotReadableError' ||
    normalized.includes('could not start') ||
    normalized.includes('in use')
  ) {
    return {
      title: 'Camera or microphone is unavailable',
      message: 'Another app or browser tab may already be using your device.',
      detail: 'Close other apps using the camera or microphone and try again, or continue without camera and microphone.',
    };
  }

  if (
    name === 'MediaDevicesError' ||
    (name === 'ConnectionError' && message.includes('Client initiated disconnect'))
  ) {
    return {
      title: 'Camera and microphone access needs attention',
      message: 'The media connection was interrupted while joining.',
      detail: 'This often happens after camera or microphone access is denied. Try granting access again, or continue without camera and microphone.',
    };
  }

  return null;
}
